import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MotionConfig, motion } from 'framer-motion';
import { LuBookOpen, LuCalendarDays, LuFolderKanban, LuMegaphone } from 'react-icons/lu';
import { useUserProfile } from '../../contexts/UserProfileContext';
import useUnseenTaskCount from '../../hooks/useUnseenTaskCount';
import { fadeUp, staggerContainer } from '../../components/ui/motionPresets';
import { useTranslation } from '../../i18n/LanguageContext';
import UserManual from './UserManual';
import ProjectsModal from '../EmployeeManagement/ProjectsModal';
import MyTasksModal from './MyTasksModal';
import WelcomeModal from './WelcomeModal';
import useCVUpdateTaskCheck from './useCVUpdateTaskCheck';
import useFirstLoginWelcome from './useFirstLoginWelcome';
import DashboardHero from './components/DashboardHero';
import TasksBanner from './components/TasksBanner';
import QuickLinkCard from './components/QuickLinkCard';
import UserGuideCard from './guide/UserGuideCard';
import UserGuidePlayer from './guide/UserGuidePlayer';
import useUserGuide from './guide/useUserGuide';

// Name to greet with, from the richest field available
const getUserName = (employeeData) => {
  if (!employeeData) return '';
  if (employeeData.fullName) return employeeData.fullName;
  if (employeeData.eName && employeeData.eSurname) return `${employeeData.eName} ${employeeData.eSurname}`;
  if (employeeData.name) return employeeData.name;
  if (employeeData.displayName) return employeeData.displayName;
  if (employeeData.eMail) return employeeData.eMail.split('@')[0];
  return '';
};

/**
 * Portal home (Tailwind). The modals portal themselves to <body> (ui/Modal),
 * which also locks page scrolling while one is open.
 */
const Dashboard = () => {
  const navigate = useNavigate();
  const { t, language } = useTranslation();
  const { employeeData, employeeId } = useUserProfile();
  const { unseenCount, refresh: refreshUnseenCount } = useUnseenTaskCount(employeeId);
  const [showWelcome, setShowWelcome] = useFirstLoginWelcome(employeeId);
  const [showManual, setShowManual] = useState(false);
  const [showProjects, setShowProjects] = useState(false);
  const [showMyTasks, setShowMyTasks] = useState(false);
  const guide = useUserGuide();

  useCVUpdateTaskCheck();

  const userName = getUserName(employeeData);

  // The profile document has no `id`; ProjectsModal needs it for the
  // employee's own patients and visits.
  const projectsEmployee = useMemo(
    () => (employeeData ? { ...employeeData, id: employeeId } : null),
    [employeeData, employeeId]
  );

  const quickLinks = [
    {
      id: 'calendar',
      title: t('dashboard.links.calendar.title'),
      description: t('dashboard.links.calendar.description'),
      icon: LuCalendarDays,
      gradient: 'tw-from-blue-500 tw-to-indigo-500 tw-shadow-blue-500/30',
      spotlightColor: 'rgba(59, 130, 246, 0.14)',
      onClick: () => navigate('/profile', { state: { activeTab: 'takvim' } }),
    },
    {
      id: 'projects',
      title: t('dashboard.links.projects.title'),
      description: t('dashboard.links.projects.description'),
      icon: LuFolderKanban,
      gradient: 'tw-from-violet-500 tw-to-fuchsia-500 tw-shadow-violet-500/30',
      spotlightColor: 'rgba(139, 92, 246, 0.14)',
      onClick: () => setShowProjects(true),
    },
    {
      id: 'announcements',
      title: t('dashboard.links.announcements.title'),
      description: t('dashboard.links.announcements.description'),
      icon: LuMegaphone,
      gradient: 'tw-from-amber-400 tw-to-orange-500 tw-shadow-amber-500/30',
      spotlightColor: 'rgba(245, 158, 11, 0.14)',
      onClick: () => navigate('/profile', { state: { activeTab: 'bildirimler' } }),
    },
    {
      id: 'manual',
      title: t('dashboard.links.manual.title'),
      description: t('dashboard.links.manual.description'),
      icon: LuBookOpen,
      gradient: 'tw-from-emerald-500 tw-to-teal-500 tw-shadow-emerald-500/30',
      spotlightColor: 'rgba(16, 185, 129, 0.14)',
      onClick: () => setShowManual(true),
    },
  ];

  const handleCloseMyTasks = () => {
    setShowMyTasks(false);
    refreshUnseenCount();
  };

  // The welcome dialog's "Kullanım Kılavuzu" opens the new-system guide
  const handleWelcomeOpenManual = () => {
    setShowWelcome(false);
    guide.open();
  };

  const handleOpenOldGuide = () => {
    guide.close();
    setShowManual(true);
  };

  return (
    <>
      <MotionConfig reducedMotion="user">
        <motion.div
          variants={staggerContainer(0.08)}
          initial="hidden"
          animate="show"
          lang={language}
          className="tw-mx-auto tw-w-full tw-max-w-6xl tw-space-y-5 tw-pb-4 tw-font-sans tw-antialiased sm:tw-space-y-6"
        >
          <motion.div variants={fadeUp}>
            <DashboardHero userName={userName} />
          </motion.div>

          <motion.div variants={fadeUp}>
            <TasksBanner unseenCount={unseenCount} onOpen={() => setShowMyTasks(true)} />
          </motion.div>

          <motion.div variants={fadeUp}>
            <UserGuideCard onOpen={guide.open} />
          </motion.div>

          <motion.section variants={fadeUp} aria-labelledby="dashboard-quick-links">
            <h2 id="dashboard-quick-links" className="tw-px-1 tw-text-sm tw-font-semibold tw-uppercase tw-tracking-[0.14em] tw-text-slate-500 dark:tw-text-slate-400">
              {t('dashboard.quickLinks')}
            </h2>
            <motion.div variants={staggerContainer(0.06)} className="tw-mt-3 tw-grid tw-gap-4 sm:tw-grid-cols-2 xl:tw-grid-cols-4">
              {quickLinks.map((link) => (
                <QuickLinkCard key={link.id} {...link} />
              ))}
            </motion.div>
          </motion.section>
        </motion.div>
      </MotionConfig>

      {/* Yeni sistem kullanım kılavuzu (video rehber) */}
      <UserGuidePlayer isOpen={guide.isOpen} start={guide.start} onClose={guide.close} onOpenOldGuide={handleOpenOldGuide} />

      {/* Eski sistemin PDF kılavuzu */}
      <UserManual isOpen={showManual} onClose={() => setShowManual(false)} />

      {/* Projects Modal */}
      {showProjects && projectsEmployee && (
        <ProjectsModal employee={projectsEmployee} onClose={() => setShowProjects(false)} />
      )}

      {/* My Tasks Modal */}
      <MyTasksModal isOpen={showMyTasks} onClose={handleCloseMyTasks} />

      {/* İlk giriş karşılama mesajı (yalnızca yeni çalışanlara, bir kez) */}
      <WelcomeModal
        isOpen={showWelcome}
        userName={userName}
        onClose={() => setShowWelcome(false)}
        onOpenManual={handleWelcomeOpenManual}
      />
    </>
  );
};

export default Dashboard;
